import React, { useState, useEffect } from 'react';
import axios from 'axios';

const DocumentationViewer = () => {
  const [docStructure, setDocStructure] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedContent, setSelectedContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);
  const [error, setError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    loadDocumentationStructure();
  }, []);

  const loadDocumentationStructure = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/documentation/structure', { 
        withCredentials: true 
      });
      
      setDocStructure(response.data);
      setError(null);
    } catch (error) {
      console.error('Failed to load documentation structure:', error);
      setError('Failed to load documentation structure');
    } finally {
      setLoading(false);
    }
  };

  const loadFileContent = async (filePath) => {
    setLoadingContent(true);
    setSelectedFile(filePath);
    
    try {
      const response = await axios.get(`/api/documentation/file`, {
        params: { path: filePath },
        withCredentials: true 
      });
      setSelectedContent(response.data);
      setError(null);
    } catch (error) {
      console.error('Failed to load file content:', error);
      setError('Failed to load file content');
      setSelectedContent(null);
    } finally {
      setLoadingContent(false);
    }
  };

  const renderFileTree = (items, level = 0) => {
    if (!items) return null;

    return Object.entries(items).map(([name, item]) => {
      const isDirectory = typeof item === 'object' && !item.isFile;
      const isSelected = selectedFile === item.path;

      return (
        <div key={name} className="select-none">
          <div
            className={`flex items-center p-2 rounded cursor-pointer hover:bg-gray-100 ${
              isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
            }`}
            style={{ paddingLeft: `${12 + level * 16}px` }}
            onClick={() => !isDirectory && loadFileContent(item.path)}
          >
            <span className="mr-2 text-sm">
              {isDirectory ? '📁' : '📝'}
            </span>
            
            <span className="flex-1 text-sm truncate" title={name}>
              {name}
            </span>
          </div>
          
          {isDirectory && (
            <div>
              {renderFileTree(item, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  const renderMarkdownContent = () => {
    if (loadingContent) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-400 border-t-transparent"></div>
            <p className="text-gray-500 mt-2">Loading document...</p>
          </div>
        </div>
      );
    }

    if (!selectedContent) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-gray-500">
            <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>Select a markdown file to view its contents</p>
          </div>
        </div>
      );
    }

    return (
      <div className="p-6 prose max-w-none">
        <div 
          className="markdown-content"
          dangerouslySetInnerHTML={{ __html: selectedContent.html }}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-400 border-t-transparent"></div>
          <p className="text-gray-500 mt-2">Loading documentation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          Documentation Error
        </h2>
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={loadDocumentationStructure}
            className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md h-[600px] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            SEEN Ecosystem Documentation
          </h2>
          <div className="flex items-center space-x-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-1"></span>
              Local Docs
            </span>
            <button
              onClick={loadDocumentationStructure}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100"
              title="Refresh"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Split Pane Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar */}
        <div className={`${sidebarCollapsed ? 'w-12' : 'w-80'} transition-all duration-300 border-r border-gray-200 flex flex-col bg-gray-50`}>
          {!sidebarCollapsed && (
            <>
              {/* File Tree */}
              <div className="flex-1 overflow-y-auto p-2">
                {docStructure && renderFileTree(docStructure)}
              </div>
            </>
          )}
          
          {/* Sidebar Toggle */}
          <div className="p-2 border-t border-gray-200 bg-white">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100 w-full"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <svg className={`w-4 h-4 mx-auto transform transition-transform ${sidebarCollapsed ? 'rotate-0' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {selectedFile && (
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">{selectedFile.split('/').pop()}</h1>
                  <p className="text-sm text-gray-500">
                    Path: {selectedFile}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex-1 overflow-y-auto">
            {renderMarkdownContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentationViewer;